import Group from 'components/utils/Group'
import React, { MouseEventHandler, PropsWithChildren, useCallback, useContext } from 'react'
import AccommodationAmenitiesAndPoliciesModal from './AccommodationAmenitiesAndPoliciesModal'
import ModalContext from 'contexts/ModalContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import AccommodationRoomTileMessage from './AccommodationRoomTileMessage'
import WalledContent from 'components/Common/WalledContent'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'

export type AccommodationRoomTileActionsState =
  'loading' |
  'sold-out-on-dates' |
  'sold-out' |
  'exceeded-capacity' |
  'sold-out-on-dates-or-exceeded-capacity' |
  'available-on-dates'

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
  state?: AccommodationRoomTileActionsState
  isWalled?: boolean
  offer: App.HotelOffer
  onAddAnotherUnitClick?: MouseEventHandler<HTMLButtonElement>
  onChangeDatesClick?: MouseEventHandler<HTMLButtonElement>
  onCheckAvailabilityClick?: MouseEventHandler<HTMLButtonElement>
}

function AccommodationRoomTileActions(props: PropsWithChildren<Props>) {
  const {
    children,
    roomPackage,
    state,
    offer,
    isWalled,
    onAddAnotherUnitClick,
    onCheckAvailabilityClick,
    onChangeDatesClick,
  } = props

  const showModal = useContext(ModalContext)

  const openAmenitiesAndPoliciesModal = useCallback(() => {
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={roomPackage} />)
  }, [roomPackage, showModal])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  return <Group direction="vertical" tabletHorizontalAlign="end" gap={12}>
    <CSSBreakpoint max="mobile">
      <TextButton size="large" horizontalOutdent="start" kind="tertiary" onClick={openAmenitiesAndPoliciesModal}>
        View amenities & policies
      </TextButton>
    </CSSBreakpoint>

    {offer.type !== OFFER_TYPE_ALWAYS_ON && (
      <WalledContent
        enforced={isWalled ?? offer.walledGarden}
        wall={<AccommodationRoomTileMessage message="members-exclusive" />}
      >
        {!!state && <AccommodationRoomTileMessage message={state} />}
      </WalledContent>
    )}

    <Group
      direction="vertical"
      verticalAlign="center"
      tabletDirection="horizontal"
      tabletHorizontalAlign="end"
      gap={8} wrap="wrap"
      noDisplayWhenEmpty
    >
      <CSSBreakpoint min="tablet">
        <TextButton size="large" kind="tertiary" onClick={openAmenitiesAndPoliciesModal}>
          View amenities & policies
        </TextButton>
      </CSSBreakpoint>
      <WalledContent
        enforced={isWalled ?? offer.walledGarden}
        wall={offer.type !== OFFER_TYPE_ALWAYS_ON && <TextButton size="large" kind="secondary" onClick={onSignUpClick}>
          Sign up for free
        </TextButton>}
      >
        {children}
        {(state === 'sold-out-on-dates' || state === 'sold-out-on-dates-or-exceeded-capacity') && onChangeDatesClick && <TextButton
          size="large"
          data-testid="room-tile-action-change-dates"
          kind="secondary"
          onClick={onChangeDatesClick}
        >
          Change your dates
        </TextButton>}
        {(state === 'exceeded-capacity' || state === 'sold-out-on-dates-or-exceeded-capacity') && onAddAnotherUnitClick && <TextButton
          size="large"
          data-testid="room-tile-action-add-another-unit"
          kind="secondary"
          onClick={onAddAnotherUnitClick}
        >
          Add another {offer.saleUnit}
        </TextButton>}
        {state === 'available-on-dates' && onCheckAvailabilityClick && <TextButton
          size="large"
          data-testid="room-tile-action-check-availability"
          kind="primary"
          onClick={onCheckAvailabilityClick}
        >
          Check availability
        </TextButton>}
      </WalledContent>
      {state === 'loading' && <LoadingAnimation size={12} />}
    </Group>
  </Group>
}

export default withOfferPageAndReduxState((offerPageState) => ({
  offer: (offerPageState.offer ?? offerPageState.bedbankOffer)!,
}))(AccommodationRoomTileActions)
